/**
 * A tab strip whose selection lives in ?tab=, for the API screens.
 *
 * NOT a copy of Status's Tabs. That one carries alarm dots, a keyboard
 * roving-tabindex and lazily-invoked bodies for a page with nine sections;
 * these two pages have three between them and need none of it. Sharing it
 * would mean exporting a component built around another page's requirements
 * and then widening it for these.
 *
 * The selection is in the URL for the same reason Status's is: a link to the
 * reference should open the reference.
 */
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

export type TabDef = { id: string; label: string; body: () => ReactNode }

export default function TabStrip({ tabs, label }: { tabs: TabDef[]; label: string }) {
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  // An unknown ?tab= shows the first rather than nothing: a typo in a pasted
  // link should not produce a blank page.
  const active = tabs.find((t) => t.id === requested) ?? tabs[0]!

  return (
    <>
      <div className="st-tabs" role="tablist" aria-label={label}>
        {tabs.map((tab) => (
          <button key={tab.id} type="button" role="tab"
                  id={`api-tab-${tab.id}`}
                  aria-controls={`api-panel-${tab.id}`}
                  aria-selected={tab.id === active.id}
                  className="st-tab"
                  onClick={() => {
                    const next = new URLSearchParams(params)
                    next.set('tab', tab.id)
                    setParams(next)
                  }}>
            {tab.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`api-panel-${active.id}`}
           aria-labelledby={`api-tab-${active.id}`}>
        {/* Called, not stored: a closed tab must not fetch. */}
        {active.body()}
      </div>
    </>
  )
}
